import { formatDistanceToNowStrict, isPast } from "date-fns";
import { toast } from "sonner";

import type { ReportRun } from "@/lib/api";
import { ApiError } from "@/lib/api";
import { formatTimestamp } from "@/lib/format";

/** The difficulty scale, 1 to 5, as the hand-in form and the staff pages word it. */
export const DIFFICULTY_LABELS: Record<number, string> = {
  1: "Very easy",
  2: "Easy",
  3: "Moderate",
  4: "Hard",
  5: "Very hard",
};

/** "4 · Hard". */
export function formatDifficulty(value: number): string {
  return `${value} · ${DIFFICULTY_LABELS[value] ?? "?"}`;
}

/** Limits the server enforces, so the form can say so before a round trip. */
export const PROBLEMS_MAX = 4000;

/** "Oct 28, 21:14 · 7 / 10 · Bob" for a run in the picker and on the record. */
export function formatRun(run: ReportRun): string {
  const result =
    run.points !== null && run.max_points !== null
      ? `${run.points} / ${run.max_points} points`
      : run.status;
  return `${formatTimestamp(run.started_at)} · ${result} · ${run.run_by} · ${run.id}`;
}

/** The code upload: one agent file, or a zip when it spans several. */
export const CODE_ACCEPT = ".py,.zip,text/x-python,application/zip";

export function isCodeFile(name: string): boolean {
  return /\.(py|zip)$/i.test(name);
}

/** "Due in 3 days" / "Was due 2 hours ago" / "No deadline". */
export function formatDue(ms: number | null): string {
  if (ms === null) return "No deadline";
  const date = new Date(ms);
  const distance = formatDistanceToNowStrict(date);
  return isPast(date) ? `Was due ${distance} ago` : `Due in ${distance}`;
}

/**
 * Open a PDF that sits behind a key. The page cannot link to it directly -- a link sends no
 * header -- so it is fetched, handed to a new tab as an object URL, and released a minute
 * later, by which time the tab has read it.
 *
 * The tab is opened before the fetch, on the click itself, because a popup opened after an
 * `await` is no longer "user initiated" and browsers block it.
 */
export async function openPdf(getUrl: () => Promise<string>): Promise<void> {
  const tab = window.open("", "_blank");
  try {
    const url = await getUrl();
    if (tab) tab.location.href = url;
    else window.location.href = url;
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (caught) {
    tab?.close();
    toast.error(caught instanceof ApiError ? caught.message : "Could not open the PDF");
  }
}

/** Save a file that sits behind a key under its own name, as `openPdf` opens one. */
export async function downloadFile(getUrl: () => Promise<string>, name: string): Promise<void> {
  try {
    const url = await getUrl();
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (caught) {
    toast.error(caught instanceof ApiError ? caught.message : "Could not download the file");
  }
}
